import React, { useCallback, useEffect, useMemo, useState, useTransition } from 'react'
import { useRealTimeData } from '../../hooks/useRealTimeData.js'
import { useFaultDetection } from '../../hooks/useFaultDetection.js'
import { useCanBusStatus } from '../../hooks/useCanBusStatus.js'
import useChartData from '../../hooks/useChartData.js'
import { fetchDashboardFields, fetchHistoricalData } from '../../utils/dashboardApi.js'
import FaultAlert from '../dashboard/FaultAlert.jsx'
import CanBusStatus from '../dashboard/CanBusStatus.jsx'
import DeviceGrid from '../dashboard/DeviceGrid.jsx'
import PresenceConfirmation from '../dashboard/PresenceConfirmation.jsx'
import { PageContainer, PageHeader, StatusIndicator } from '../common'
import '../../styles/animations.css'

/**
 * Real-time monitoring dashboard for the Revatek Altion regulator.
 * Implements historical data loading first, then real-time updates.
 */
function DashboardPage() {
  const [historicalState, setHistoricalState] = useState({
    loaded: false,
    loading: true,
    error: null,
  })
  
  // Track when we last received data in browser time to avoid device clock sync issues
  const [lastDataUpdateTime, setLastDataUpdateTime] = useState(null)
  
  const [_isPending, startTransition] = useTransition()

  // Use the new combined real-time data hook
  const { 
    data: realTimeData, 
    faults: faultsData,
    loading: dataLoading, 
    error: dataError, 
    isConnected, 
    retryConnection, 
    startUpdates, 
    stopUpdates,
    pollingController,
  } = useRealTimeData()
  
  // Pass faults data from the combined endpoint to the fault detection hook
  const { faults, loading: faultsLoading, hasFaults } = useFaultDetection(faultsData)
  const { status: canBusStatus, connectionMode, translatedChargeStage } = useCanBusStatus(realTimeData)
  const { getChartData, processRealTimeData, isChartVisible, updateChartVisibility, updateChartData } = useChartData()

  const loadHistoricalData = useCallback(async () => {
    setHistoricalState(prev => ({ ...prev, loading: true, error: null }))

    try {
      const fields = await fetchDashboardFields()
      if (!fields?.length) {
        setHistoricalState({ loaded: true, loading: false, error: null })
        startUpdates() // Always start real-time updates
        return
      }

      const historicalDataBatch = {}
      await Promise.all(fields.map(async ({ device, measurement }) => {
        const key = `${device}-${measurement}`
        try {
          const historicalData = await fetchHistoricalData(device, measurement, 30)
          if (historicalData?.length) {
            const chartPoints = historicalData
              .filter(item => item._value != null && !isNaN(item._value))
              .map(item => ({
                x: new Date(item._time).getTime(),
                y: item._value,
                timestamp: item._time,
                value: item._value,
              }))
              .sort((a, b) => a.x - b.x)

            if (chartPoints.length) {
              historicalDataBatch[key] = chartPoints
            }
          }
        } catch (error) {
          console.error(`Failed to load historical data for ${key}:`, error)
        }
      }))

      if (Object.keys(historicalDataBatch).length > 0) {
        updateChartData(historicalDataBatch)
      }

      setHistoricalState({ loaded: true, loading: false, error: null })
      startUpdates()
    } catch (error) {
      console.error('Failed to load historical data:', error)
      setHistoricalState(prev => ({ ...prev, loading: false, error: error.message }))
      startUpdates() // Always start real-time updates even if historical fails
    }
  }, [updateChartData, startUpdates])

  // Consolidated useEffect for initialization and data processing
  useEffect(() => {
    stopUpdates()
    loadHistoricalData()
    return () => stopUpdates()
  }, [loadHistoricalData, stopUpdates])

  // Process real-time data updates with throttling to prevent performance issues
  useEffect(() => {
    if (realTimeData?.length > 0) {
      // Update last data received timestamp when we get new data
      setLastDataUpdateTime(Date.now())
      
      // Throttle expensive operations to improve performance
      const timeoutId = setTimeout(() => {
        const newFields = processRealTimeData(realTimeData)
        if (newFields.size > 0) {
          startTransition(() => {
            updateChartVisibility()
          })
        }
      }, 100) // Throttle to every 100ms
      
      return () => clearTimeout(timeoutId)
    }
  }, [realTimeData, processRealTimeData, updateChartVisibility])

  const hasRecentData = useCallback((deviceMeasurementKey) => {
    if (!realTimeData?.length) return false
    const [device, measurement] = deviceMeasurementKey.split('-')
    const recentData = realTimeData.find(item => 
      item.device === device && 
      item.measurement === measurement &&
      (Date.now() - new Date(item._time).getTime()) < 30000,
    )
    return !!recentData
  }, [realTimeData])

  const hasAnyData = realTimeData?.length > 0
  const isNoDataDetected = !dataLoading && !historicalState.loading && !hasAnyData && historicalState.loaded

  const getDashboardStatus = useMemo(() => {
    if (historicalState.loading) {
      return { status: 'loading', text: 'Loading historical data...', animation: 'pulse' }
    }
    
    if (historicalState.error) {
      return { status: 'warning', text: 'Historical data failed, using real-time only', animation: 'none' }
    }
    
    if (!historicalState.loaded && dataLoading) {
      return { status: 'loading', text: 'Loading data...', animation: 'pulse' }
    }
    
    if (dataLoading) {
      return { status: 'loading', text: 'Loading real-time data...', animation: 'pulse' }
    }
    
    if (dataError) {
      return { status: 'error', text: 'Data connection error', animation: 'none' }
    }
    
    if (hasAnyData) {
      // Use browser timestamp of when we last received data instead of device timestamps
      // This avoids issues with device clock synchronization
      const timeSinceLastUpdate = lastDataUpdateTime ? (Date.now() - lastDataUpdateTime) : Infinity
      const isDataFresh = timeSinceLastUpdate < 180000  // 180 seconds = 3 minutes
      
      return isDataFresh 
        ? { status: 'success', text: 'Live data', animation: 'glow' }
        : { status: 'warning', text: 'Stale data', animation: 'none' }
    }
    
    return { status: 'info', text: 'No data', animation: 'none' }
  }, [historicalState, dataLoading, dataError, hasAnyData, lastDataUpdateTime])

  return (
    <PageContainer>
      <PageHeader 
        title="Dashboard"
        statusIndicator={
          <StatusIndicator 
            status={getDashboardStatus.status}
            text={getDashboardStatus.text}
            animation={getDashboardStatus.animation}
          />
        }
      />

      <FaultAlert 
        faults={faults} 
        isVisible={hasFaults && !faultsLoading} 
      />

      {/* Show data connectivity issues but don't block the dashboard */}
      {(dataError || historicalState.error) && (historicalState.loaded || realTimeData?.length > 0) && (
        <div className="alert alert-warning d-flex justify-content-between align-items-start mb-3">
          <div>
            <i className="bi bi-exclamation-triangle-fill me-2"></i>
            <strong>Data Connection Issues:</strong>{' '}
            {dataError && <span>Real-time data: {dataError}. </span>}
            {historicalState.error && <span>Historical data: {historicalState.error}. </span>}
            Dashboard will continue with available data.
          </div>
          <div>
            <button 
              className="btn btn-outline-warning btn-sm" 
              onClick={() => {
                if (retryConnection) retryConnection()
                if (historicalState.error) loadHistoricalData()
              }}
              title="Retry data connections"
            >
              <i className="bi bi-arrow-clockwise me-1"></i>Retry Connection
            </button>
          </div>
        </div>
      )}

      <CanBusStatus 
        status={canBusStatus} 
        connectionMode={connectionMode}
        translatedChargeStage={translatedChargeStage}
        isVisible={!dataLoading && !historicalState.loading && (isConnected || isNoDataDetected)}
        isNoDataDetected={isNoDataDetected}
      />

      {/* User presence confirmation modal */}
      <PresenceConfirmation 
        isVisible={pollingController?.needsPresenceConfirmation || false}
        onConfirm={pollingController?.confirmPresence}
        onPause={pollingController?.pauseUpdates}
      />

      {!isNoDataDetected && (historicalState.loaded || realTimeData?.length > 0) && (
        <DeviceGrid 
          data={realTimeData}
          isLoading={dataLoading}
          error={(dataError || historicalState.error) && (historicalState.loaded || realTimeData?.length > 0) ? null : dataError}
          getChartData={getChartData}
          canBusData={canBusStatus}
          isChartVisible={isChartVisible}
          hasRecentData={hasRecentData}
          realTimeData={realTimeData}
          retryConnection={retryConnection}
        />
      )}
    </PageContainer>
  )
}

export default React.memo(DashboardPage)
